/**
 * Login Page — GenesisBox
 * Firebase Auth (Google provider) + Microsoft OAuth. Dev Login is localhost-only.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { useAuth } from '@hooks/auth/useAuth';
import { useOrg } from '@hooks/auth/useOrg';
import { getDevLoginCredentials, isDevLoginEnabled } from '@config/oauth';
import { signInWithGoogle, signInWithMicrosoft, checkGoogleRedirectResult } from '@config/firebase';
import { config } from '@config/environment';
import { setActiveOrgId } from '@services/storage/savedConnectionsStorage';
import { AUTH_SESSION_EXPIRED_MESSAGE, AUTH_SESSION_NOTICE_KEY } from '@services/api/apiConfig';

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const { setOrg } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const showDevLogin = isDevLoginEnabled();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(() => {
    const message = localStorage.getItem(AUTH_SESSION_NOTICE_KEY);
    if (message) localStorage.removeItem(AUTH_SESSION_NOTICE_KEY);
    return message;
  });
  const [busy, setBusy] = useState(false);
  // true while checkGoogleRedirectResult() runs on mount (mobile redirect return)
  const [checkingRedirect, setCheckingRedirect] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('session') === 'expired') {
      setNotice(AUTH_SESSION_EXPIRED_MESSAGE);
    }
  }, [location.search]);

  // Complete a mobile redirect sign-in when returning from Google.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await checkGoogleRedirectResult();
        if (cancelled) return;
        if (result) await exchangeFirebaseToken(result.idToken);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Google sign-in failed after redirect');
      } finally {
        if (!cancelled) setCheckingRedirect(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) navigate(`/drive${location.search}`, { replace: true });
  }, [isAuthenticated, location.search, navigate]);

  async function exchangeFirebaseToken(idToken: string): Promise<void> {
    const res = await fetch(`${config.apiBaseUrl}/auth/firebase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Sign-in failed (${res.status})`);
    }

    const data = await res.json();
    if (!data.token) throw new Error('No token returned from server');

    await login(data.token, '');

    if (data.org) {
      setOrg({
        id: data.org.id,
        name: data.org.name,
        role: data.org.role,
        storageBackend: data.org.storageBackend || 'r2',
        memberCount: data.org.memberCount,
        isApproved: !!data.org.isApproved,
        status: data.org.status || 'approved',
        isAdmin: !!data.org.isAdmin,
        adminEnabled: !!data.org.adminEnabled,
        platformRole: data.org.platformRole === 'platform_owner' ? 'platform_owner' : null,
        canManageMembers: !!data.org.canManageMembers,
        orgs: data.orgs || [],
      });
      setActiveOrgId(data.org.id);
    }

    navigate(`/drive${location.search}`, { replace: true });
  }

  const handleGoogleSignIn = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      // Popup first; falls back to full-page redirect on mobile (navigates away).
      const { idToken } = await signInWithGoogle();
      await exchangeFirebaseToken(idToken);
    } catch (err: any) {
      console.error('[GenesisBox] Google sign-in failed:', err);
      setError(err?.message || 'Google sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const { idToken } = await signInWithMicrosoft();
      await exchangeFirebaseToken(idToken);
    } catch (err: any) {
      console.error('[GenesisBox] Microsoft sign-in failed:', err);
      setError(err?.message || 'Microsoft sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDevLogin = async () => {
    setNotice(null);
    const { username, password } = getDevLoginCredentials();
    await login(username, password);

    const basicToken = btoa(`${username}:${password}`);
    const res = await fetch(`${config.apiBaseUrl}/auth/me`, {
      headers: { Authorization: `Basic ${basicToken}` },
    });

    if (res.ok) {
      const data = await res.json();
      setOrg({
        id: data.org.id,
        name: data.org.name,
        role: data.org.role,
        storageBackend: data.org.storageBackend || 'r2',
        memberCount: data.org.memberCount,
        isApproved: !!data.org.isApproved,
        status: data.org.status || 'approved',
        isAdmin: !!data.org.isAdmin,
        adminEnabled: !!data.org.adminEnabled,
        platformRole: data.org.platformRole === 'platform_owner' ? 'platform_owner' : null,
        canManageMembers: !!data.org.canManageMembers,
        orgs: data.orgs || [],
      });
      setActiveOrgId(data.org.id);
    }

    navigate(`/drive${location.search}`, { replace: true });
  };

  const signInBusy = busy || checkingRedirect;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'transparent',
        p: 2,
      }}
    >
      <Card
        className="glass-card"
        sx={{ maxWidth: 400, width: '100%', borderRadius: 4, overflow: 'hidden' }}
      >
        <CardContent sx={{ p: { xs: 3.5, sm: 4.5 } }}>
          {/* Brand mark */}
          <Stack spacing={2.5} alignItems="center" sx={{ mb: 4 }}>
            <Box
              component="img"
              src="/icon-192.png?v=4"
              alt="GenesisBox"
              sx={{
                width: { xs: 68, sm: 76 },
                height: { xs: 68, sm: 76 },
                minWidth: 68,
                minHeight: 68,
                borderRadius: 3,
                border: '1px solid rgba(143,245,255,0.2)',
                objectFit: 'cover',
              }}
            />
            <Stack spacing={0.75} alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', textAlign: 'center' }}>
                Welcome to GenesisBox
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', textAlign: 'center', lineHeight: 1.6 }}>
                Sign in to reach your files and shared folders.
              </Typography>
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {notice && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              {notice}
            </Alert>
          )}

          {checkingRedirect && !error && (
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 2, py: 1 }}
            >
              <CircularProgress size={14} sx={{ color: '#8ff5ff' }} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Finishing sign-in…
              </Typography>
            </Stack>
          )}

          <Stack spacing={1.5}>
            <Button
              variant="contained"
              fullWidth
              disabled={signInBusy}
              onClick={() => { void handleGoogleSignIn(); }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.06)',
                color: 'text.primary',
                border: '1px solid rgba(143,245,255,0.14)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                py: 1.4,
                '&:hover': { bgcolor: 'rgba(143,245,255,0.08)', borderColor: 'rgba(143,245,255,0.34)' },
              }}
              startIcon={
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
              }
            >
              {busy ? 'Signing in…' : 'Continue with Google'}
            </Button>

            <Button
              variant="contained"
              fullWidth
              disabled={signInBusy}
              onClick={() => { void handleMicrosoftSignIn(); }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.06)',
                color: 'text.primary',
                border: '1px solid rgba(143,245,255,0.14)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                py: 1.4,
                '&:hover': { bgcolor: 'rgba(143,245,255,0.08)', borderColor: 'rgba(143,245,255,0.34)' },
              }}
              startIcon={
                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
              }
            >
              Continue with Microsoft
            </Button>

            {showDevLogin && (
              <Button
                variant="text"
                fullWidth
                disabled={signInBusy}
                onClick={() => { void handleDevLogin(); }}
                sx={{
                  color: 'text.secondary',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  py: 1,
                  mt: 0.5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: 'text.primary' },
                }}
              >
                Dev login (localhost)
              </Button>
            )}
          </Stack>

          <Typography
            sx={{
              fontSize: 12.5,
              color: 'text.secondary',
              textAlign: 'center',
              lineHeight: 1.7,
              mt: 4,
            }}
          >
            New here? You'll be connected to your organization right after you sign in.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
